import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { resubscriptionGemsReward } from 'src/replicant/chatbot/chatbot.ruleset';
import { isLiffFriendshipApiSupported } from 'src/lib/gcinstant';
import { requestAndVerifyChatbotSubscription } from './resubscribe.sequence';
import { AssetGroup } from '@play-co/timestep-core';
import sounds from 'src/lib/sounds';

import type { ResubscribeCloseReason } from 'src/replicant/chatbot/chatbot.analytics.schema';

type CloseHandler = (result: ResubscribeCloseReason) => void;

export default class PopupResubscribePrompt extends PopupBasic<CloseHandler> {
  private close: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...opts,
      close: () => opts.close('close_button'),
      zIndex: 10000,
      width: 558,
      height: 732,
      tracking: { feature: 'resubscribe', $subFeature: 'resubscribe-prompt' },
    });

    this.close = opts.close;

    this.message.updateOpts({
      y: this.box.style.height - 280,
      height: 160,
      wordWrap: false,
    });

    new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/resubscribe/friend_image.png',
      x: this.box.style.width / 2 - 10,
      y: 220,
      width: 493,
      height: 247,
      centerOnOrigin: true,
    });

    const rewardPanel = new ImageSliceView({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 400,
      width: 248,
      height: 102,
      image: 'assets/ui/shared/frames/inset_panel.png',
      slices: {
        left: 25,
        right: 25,
        top: 25,
        bot: 25,
      },
      centerOnOrigin: true,
    });

    new ImageScaleView({
      superview: rewardPanel,
      scaleMethod: 'contain',
      image: 'assets/ui/hud/icons/hud_gem.png',
      width: 74,
      height: 74,
      x: 24,
      y: 14,
    });

    new LangBitmapFontTextView({
      superview: rewardPanel,
      size: 42,
      font: 'Body',
      height: 68,
      width: 196,
      verticalAlign: 'center',
      align: 'center',
      x: 66,
      y: 14,
      localeText: () => `X ${50}`,
    });

    const subButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      localeText: () => i18n('resubscribe.prompt.cta'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.handleResubscribe(subButton);
      },
      icon: {
        image: 'assets/ui/resubscribe/add_friend_icon.png',
        width: 57,
        height: 52,
        offset: {
          x: -160,
          y: 0,
        },
      },
      font: 'Body',
      fontSize: 42,
      width: 422,
      height: 82,
      x: this.box.style.width / 2,
      y: this.box.style.height - 70,
      labelOffsetY: -4,
      labelPaddingX: 0,
      labelPaddingY: 0,
      centerOnOrigin: true,
      tracking: {
        feature: 'resubscribe-prompt',
        $subFeature: 'resubscribe-prompt-ok',
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/resubscribe/friend_image.png',
      'assets/ui/shared/frames/inset_panel.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/resubscribe/add_friend_icon.png',
    ]);
  }

  /**
   * LIFF path: requests friendship, closes with 'subscribe' or re-enables button.
   * Legacy path: closes with 'subscribe' — the sequence handles subscribeBotAsync.
   */
  private async handleResubscribe(button: ButtonScaleViewWithText) {
    button.setDisabled(true);

    if (isLiffFriendshipApiSupported()) {
      const isSubscribedToChatbot = await requestAndVerifyChatbotSubscription();
      if (isSubscribedToChatbot) {
        this.close('subscribe');
      } else {
        button.setDisabled(false);
      }
    } else {
      this.close('subscribe');
    }
  }

  public init(_: {}) {
    super.init({
      title: i18n('resubscribe.prompt.title'),
      message: i18n('resubscribe.prompt.message', {
        value: resubscriptionGemsReward,
      }),
    });
  }
}
